import { PendingInvitation, WorkspaceMember } from 'models/user/userWorkspaceDTOs'

import { GRAY_900 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { TIMEOUT_AFTER_SUCCESSFUL_CALLBACK } from 'constants/application'
import { logAnalyticsEvent } from 'utils/analytics'
import { useEffect } from 'react'
import { useResendWorkspaceInvitation } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'

/**
 * @interface Props Input properties
 */
interface Props {
  /** Workspace ID */
  workspaceId: string
  /** Invited member data */
  invitation: PendingInvitation
  /** User's workspace data */
  workspaceDataOfUser?: WorkspaceMember
  /** User's workspace data */
  size?: 'xs' | 'lg'
  /** Button type */
  type?: React.ComponentProps<typeof MUIButton>['type']
}

/**
 * @component Resend invitation to specific user
 * @example
 * <ResendInvitationButton workspaceId={workspaceId} memberEmail={email} />
 */
export const ResendInvitationButton: React.FC<Props> = ({ workspaceId, invitation, workspaceDataOfUser, size = 'xs', type = 'defaultPrimary' }) => {
  const resendInvitation = useResendWorkspaceInvitation()
  const { t } = useTranslation(['workspace_profile'])
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()

  // Show toast messages for success and error
  useEffect(() => {
    if (resendInvitation.isSuccess) {
      spawnSuccessToast(t('invitation_sent'))
      window.setTimeout(() => {
        resendInvitation.reset()
      }, TIMEOUT_AFTER_SUCCESSFUL_CALLBACK)
    }
    if (resendInvitation.isError) {
      spawnErrorToast(t('invitation_resend_error'))
      resendInvitation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resendInvitation.isSuccess, resendInvitation.isError])

  return (
    <MUIButton
      variant="text"
      type="secondaryNoBorder"
      size="xs"
      onClick={() => {
        if (resendInvitation.isPending) return

        resendInvitation.mutate({ workspaceId, invitationId: invitation.id })
        !!workspaceDataOfUser &&
          logAnalyticsEvent('Resend Invitation', {
            userId: workspaceDataOfUser.userId,
            userRole: workspaceDataOfUser.role,
            planId: workspaceId,
            invitedUserEmail: invitation.email,
          })
      }}
      sx={{
        cursor: 'pointer',
        fontSize: '1.2rem',
        color: GRAY_900,
        textDecoration: 'underline !important',
        fontWeight: 550,
        padding: 0,
        '&:hover': {
          bgcolor: 'transparent',
        },
      }}
    >
      {t('resend_invitation')}
    </MUIButton>
  )
}
